import {
  AttachMoney,
  Close,
  Person,
  Business,
  Schedule,
  BoltOutlined,
  CheckCircleOutlined,
  CancelOutlined,
} from '@mui/icons-material';
import { Button, CardContainer, MetricCard } from 'finara-components';
import { useState } from 'react';
import toast from 'react-hot-toast';

import { useApproveCredit, useUpdateCreditStatus } from '@/hooks/queries/useCredits';
import { Credit, CreditStatus } from '@/types/credits';
import { formatCurrency } from '@/utils/formatters';

interface CreditDetailModalProps {
  credit: Credit | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (updatedCredit: Credit) => void;
}

export const CreditDetailModal: React.FC<CreditDetailModalProps> = ({
  credit,
  isOpen,
  onClose,
  onUpdate,
}) => {
  const [comments, setComments] = useState('');
  const approveCreditMutation = useApproveCredit();
  const updateCreditStatusMutation = useUpdateCreditStatus();

  if (!isOpen || !credit) return null;

  const getStatusLabel = (status: CreditStatus): string => {
    switch (status) {
      case 'active':
        return 'activo';
      case 'pending':
        return 'pendiente';
      case 'rejected':
        return 'rechazado';
      case 'expired':
        return 'expirado';
      case 'closed':
        return 'cerrado';
      default:
        return status;
    }
  };

  const handleApprove = async () => {
    try {
      const approvedCredit = await approveCreditMutation.mutateAsync(credit.id);
      toast.success('Solicitud aprobada exitosamente');
      onUpdate?.(approvedCredit);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al aprobar la solicitud';
      toast.error(errorMessage);
    }
  };

  const handleReject = async () => {
    if (!comments.trim()) {
      toast.error('El motivo de rechazo es requerido');
      return;
    }
    try {
      const rejectedCredit = await updateCreditStatusMutation.mutateAsync({
        creditId: credit.id,
        statusUpdate: {
          status: 'rejected',
          rejected_reason: comments,
        },
      });
      toast.success('Solicitud rechazada exitosamente');
      onUpdate?.(rejectedCredit);
      onClose();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Error al rechazar la solicitud';
      toast.error(errorMessage);
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:p-0">
        {/* Backdrop */}
        <div
          className="fixed inset-0 transition-opacity bg-black opacity-60"
          onClick={onClose}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              onClose();
            }
          }}
        />

        {/* Modal */}
        <div className="relative w-full max-w-6xl mx-auto bg-surface rounded-lg shadow-xl transform transition-all">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-border">
            <div className="text-left">
              <h2 className="text-xl font-semibold text-brand-white">
                Solicitud ADV-001 - {credit.user?.name || 'Usuario no disponible'}
              </h2>
              <p className="text-sm text-muted">
                Detalles completos de la solicitud de adelanto
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-muted hover:text-muted transition-colors"
            >
              <Close />
            </button>
          </div>

          {/* Content */}
          <div className="p-6 max-h-[80vh] overflow-y-auto">
            {/* Metric Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
              <MetricCard
                title="Monto Solicitado"
                value={formatCurrency(credit.amount)}
                icon={<AttachMoney />}
                variant="white"
              />
              <MetricCard
                title="Estado"
                value={getStatusLabel(credit.status)}
                icon={<Schedule />}
                variant="white"
              />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Employee Information */}
              <CardContainer>
                <div className="flex items-center mb-4">
                  <Person className="text-muted mr-2" />
                  <h3 className="text-lg font-semibold text-brand-white">Información del Empleado</h3>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="block text-sm font-medium text-muted">Nombre</p>
                      <p className="text-brand-white">{credit.user?.name || 'No disponible'}</p>
                    </div>
                    <div>
                      <p className="block text-sm font-medium text-muted">Empresa</p>
                      <p className="text-brand-white">
                        {credit.organization?.name || 'No disponible'}
                      </p>
                    </div>
                    <div>
                      <p className="block text-sm font-medium text-muted">Email</p>
                      <p className="text-brand-white">{credit.user?.email || 'No disponible'}</p>
                    </div>
                  </div>
                </div>
              </CardContainer>

              {/* Request Details */}
              <CardContainer>
                <div className="flex items-center mb-4">
                  <Business className="text-muted mr-2" />
                  <h3 className="text-lg font-semibold text-brand-white">Detalles de la Solicitud</h3>
                </div>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="block text-sm font-medium text-muted">Monto Solicitado</p>
                      <p className="text-brand-white">{formatCurrency(credit.amount)}</p>
                    </div>
                    <div>
                      <p className="block text-sm font-medium text-muted">Fecha de Inicio</p>
                      <p className="text-brand-white">
                        {new Date(credit.request_at).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                    <div>
                      <p className="block text-sm font-medium text-muted">Fecha de Fin</p>
                      <p className="text-brand-white">
                        {new Date(credit.due_date).toLocaleDateString('es-MX')}
                      </p>
                    </div>
                  </div>
                </div>
              </CardContainer>
            </div>

            {/* Actions */}
            {credit.status === 'pending' && (
              <CardContainer className="mt-8">
                <div className="text-left flex items-center text-lg font-semibold text-brand-white mb-4">
                  <BoltOutlined className="text-muted mr-2" />
                  <h3>Acciones</h3>
                </div>
                <div className="space-y-4">
                  <div>
                    <p className="text-left block text-sm font-medium text-muted mb-4">
                      Comentarios del Analista
                    </p>
                    <textarea
                      value={comments}
                      onChange={e => setComments(e.target.value)}
                      placeholder="Agregar comentarios sobre la evaluación..."
                      className="w-full px-3 py-2 border border-border rounded-md bg-surface text-brand-white placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-brand-blue focus:border-transparent"
                      rows={3}
                    />
                  </div>
                  <div className="flex space-x-4">
                    <Button
                      variant="black"
                      onClick={() => void handleApprove()}
                      startIcon={<CheckCircleOutlined />}
                      className="flex-1 bg-green-700 border-none"
                    >
                      Aprobar Solicitud
                    </Button>
                    <Button
                      variant="black"
                      onClick={() => void handleReject()}
                      startIcon={<CancelOutlined />}
                      className="flex-1"
                    >
                      Rechazar Solicitud
                    </Button>
                  </div>
                </div>
              </CardContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
